import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n";

const SECTIONS = [
  { slug: "owners", key: "nav.owners" },
  { slug: "developers", key: "nav.developers" },
  { slug: "acquirers", key: "nav.acquirers" },
];

const PAGES = [
  { to: "/buyers", slug: "buyers", key: "nav.buyers" },
  { to: "/institutional", slug: "institutional", key: "nav.institutional" },
];

export const Nav = ({ onBookCall }) => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open]);

  const linkCls =
    "font-mono-label text-[11px] tracking-[0.22em] uppercase text-[#EDE7DA]/60 hover:text-[#B8894B] transition-colors duration-300";

  const items = [
    ...SECTIONS.map((s) => ({ ...s, href: onHome ? `#${s.slug}` : `/#${s.slug}` })),
    ...PAGES.map((p) => ({ ...p })),
    { slug: "approach", key: "nav.approach", href: onHome ? "#approach" : "/#approach" },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-[background-color,border-color] duration-300 ${
        scrolled || open
          ? "bg-[#0E1613]/85 backdrop-blur-md border-b border-[#1A2A24]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-wordmark" className="leading-none flex items-center gap-3.5">
          <img
            src="/logo-mark.png"
            alt="Novare Estates monogram"
            className="h-9 w-9 md:h-10 md:w-10"
          />
          <span>
            <span className="block font-display text-lg md:text-xl tracking-tight text-[#EDE7DA]">
              NOVARE ESTATES
            </span>
            <span className="block font-mono-label text-[9px] md:text-[10px] tracking-[0.28em] text-[#B8894B] mt-1.5">
              {t("nav.tagline")}
            </span>
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-9">
          {SECTIONS.map((l) => (
            <a
              key={l.slug}
              href={onHome ? `#${l.slug}` : `/#${l.slug}`}
              data-testid={`nav-link-${l.slug}`}
              className={linkCls}
            >
              {t(l.key)}
            </a>
          ))}
          {PAGES.map((p, i) => (
            <Link
              key={p.slug}
              to={p.to}
              data-testid={`nav-link-${p.slug}`}
              className={`${linkCls} ${i === 0 ? "border-l border-[#1A2A24] pl-9" : ""} ${
                pathname.startsWith(p.to) ? "text-[#B8894B]" : ""
              }`}
            >
              {t(p.key)}
            </Link>
          ))}
          <a
            href={onHome ? "#approach" : "/#approach"}
            data-testid="nav-link-approach"
            className={linkCls}
          >
            {t("nav.approach")}
          </a>
        </nav>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="nav-book-call-cta"
            onClick={onBookCall}
            className="hidden md:block font-mono-label text-[11px] tracking-[0.2em] uppercase bg-[#B8894B] text-[#0E1613] px-5 py-2.5 hover:bg-[#EDE7DA] transition-colors duration-300"
          >
            {t("cta.bookCall")}
          </button>
          <button
            type="button"
            data-testid="nav-menu-button"
            aria-label={open ? t("cta.close") : "Menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="md:hidden w-11 h-11 -mr-2 flex items-center justify-center text-[#EDE7DA]"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {createPortal(
        <AnimatePresence>
          {open && (
          <motion.div
            key="nav-mobile-menu"
            data-testid="nav-mobile-menu"
            className="fixed inset-0 top-20 z-30 md:hidden bg-[#0E1613]/[0.98] backdrop-blur-xl flex flex-col px-6 pb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <nav className="flex-1 flex flex-col justify-center gap-2 overflow-y-auto">
              {items.map((l, i) => {
                const inner = (
                  <>
                    <span className="font-mono-label text-[10px] tracking-[0.25em] text-[#B8894B]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display text-3xl tracking-tight text-[#EDE7DA]">
                      {t(l.key)}
                    </span>
                  </>
                );
                const cls = "flex items-baseline gap-5 py-3 min-h-[44px]";
                return (
                  <motion.div
                    key={l.slug}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {l.to ? (
                      <Link
                        to={l.to}
                        data-testid={`nav-mobile-link-${l.slug}`}
                        onClick={() => setOpen(false)}
                        className={cls}
                      >
                        {inner}
                      </Link>
                    ) : (
                      <a
                        href={l.href}
                        data-testid={`nav-mobile-link-${l.slug}`}
                        onClick={() => setOpen(false)}
                        className={cls}
                      >
                        {inner}
                      </a>
                    )}
                  </motion.div>
                );
              })}
            </nav>
            <motion.div
              className="border-t border-[#1A2A24] pt-6 flex items-center justify-between"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <span className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#EDE7DA]/45">
                {t("lang.title")}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="nav-lang-en"
                  onClick={() => setLang("en")}
                  className={`min-h-[44px] px-5 font-mono-label text-[11px] tracking-[0.2em] uppercase border transition-colors duration-200 ${
                    lang === "en"
                      ? "border-[#B8894B] text-[#B8894B]"
                      : "border-[#1A2A24] text-[#EDE7DA]/50"
                  }`}
                >
                  EN
                </button>
                <button
                  type="button"
                  data-testid="nav-lang-or"
                  onClick={() => setLang("or")}
                  className={`min-h-[44px] px-5 font-mono-label text-[11px] tracking-[0.2em] uppercase border transition-colors duration-200 ${
                    lang === "or"
                      ? "border-[#B8894B] text-[#B8894B]"
                      : "border-[#1A2A24] text-[#EDE7DA]/50"
                  }`}
                >
                  ଓଡ଼ିଆ
                </button>
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
};
